import React, { createContext, useState, useContext, ReactNode, useEffect, useMemo, useCallback } from 'react'

import { NotFound } from '@fe-toolkit/web-components'

import MainLayout from '@/layouts/Main/Main'
import FullPageLoader from '@/components/molecules/FullPageLoader'

/**
 * Context value provided by ErrorProvider.
 */
type ErrorContextProps = {
  errorCode?: number | null
  setErrorCode: (errorCode: number) => void
  clearErrorState: () => void
  errorMessage?: string | null
  setErrorMessage: (errorMessage: string) => void
  isRedirectingToLogin: boolean
  setIsRedirectingToLogin: (isRedirecting: boolean) => void
}

type ErrorProviderProps = {
  children: ReactNode
}

/** Context for the error state management. */
const ErrorContext = createContext<ErrorContextProps | undefined>(undefined)

/**
 * A provider component that manages the error state and conditionally renders the ErrorComponent.
 * @param {ErrorProviderProps} props - The props for the ErrorProvider component.
 * @returns {React.ReactElement} The ErrorProvider wrapped children or the ErrorComponent if an error code is set.
 */
export const ErrorProvider = ({ children }: ErrorProviderProps): React.ReactElement => {
  const [errorCode, setErrorCode] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isRedirectingToLogin, setIsRedirectingToLogin] = useState<boolean>(false)

  const clearErrorState = useCallback((): void => {
    setErrorCode(null)
    setErrorMessage(null)
  }, [])

  useEffect(() => {
    window.addEventListener('popstate', clearErrorState)

    return () => window.removeEventListener('popstate', clearErrorState)
  }, [clearErrorState])

  const value = useMemo(
    () => ({
      errorCode,
      setErrorCode,
      clearErrorState,
      errorMessage,
      setErrorMessage,
      isRedirectingToLogin,
      setIsRedirectingToLogin,
    }),
    [clearErrorState, errorCode, errorMessage, isRedirectingToLogin],
  )

  // While a login redirect is in flight, show a loader instead of any page content - this takes
  // priority over errorCode since the browser is about to navigate away regardless.
  return (
    <ErrorContext.Provider value={value}>
      {isRedirectingToLogin ? (
        <MainLayout>
          <FullPageLoader />
        </MainLayout>
      ) : errorCode ? (
        <MainLayout>
          <NotFound />
        </MainLayout>
      ) : (
        children
      )}
    </ErrorContext.Provider>
  )
}

/**
 * Custom hook to access the error context.
 * Must be used within a component tree that has ErrorProvider as a parent.
 * @returns {ErrorContextProps} The error context object.
 */
export const useError = (): ErrorContextProps => {
  const context = useContext(ErrorContext)

  if (!context) {
    throw new Error('useError must be used within an ErrorProvider')
  }

  return context
}
